// Orders.js

import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { IoFilter } from "react-icons/io5";
import { FaSearch, FaSort, FaExchangeAlt, FaSync, FaTruck } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Select from 'react-select';
import { getOrders, changeOrderStatus, getAvailableDrivers, assignDriverToOrder, toggleAutoConfirmOnlineOrders, getDeliverySettings, updateDeliverySettings, getDeliveredOrdersTotals, fetchBranchDataById, getMarketplaceOrders, updateMarketplaceOrderStatus } from "../../services/ClientService";
import StatusChangeModal from "../FunctionalComponents/StatusChangeModal";
import TransferOrderModal from "../FunctionalComponents/TransferOrderModal";
import { formatDateToLong } from "../../utils/dateUtils";
import { calculatePayableFromDiscountedTotal, getFinancialDiscounts } from "../../utils/orderBillingHelpers";
import { adaptMarketplaceOrderForCard, MARKETPLACE_ORDER_TABS, MARKETPLACE_STATUS_FLOW } from "../../utils/marketplaceOrderAdapter";
import apiRequest from "../../services/apiService";
import { toast } from "react-toastify";

const STATUS_FLOW = [
    "Received",
    "Confirmed",
    "Shipped",
    "Out For Delivery",
    "Delivered",
    "Cancelled"
];

const ORDER_TABS = [
    { label: "Online Orders", apiValue: "online" },
    { label: "Offline Orders", apiValue: "offline" },
    { label: "All", apiValue: "All" },
    { label: "Received", apiValue: "Received" },
    { label: "Confirmed", apiValue: "Confirmed" },
    { label: "Shipped", apiValue: "Shipped" },
    { label: "OutForDelivery", apiValue: "Out For Delivery" }, // label for UI, apiValue for API
    { label: "Delivered", apiValue: "Delivered" },
    { label: "Cancelled", apiValue: "Cancelled" }
];

const Orders = ({
    classOuterDiv,
    classInnerDiv,
    setShowStatusModal: _unused, // not used, manage locally
    setExpandedCard,
    expandedCard,
}) => {
    const navigate = useNavigate();

    // Helper function to get order item image with fallback
    const getOrderItemImage = (item) => {
        if (!item) return require("../../../src/no-image-icon-23494.png");
        if (typeof item.images?.preview === 'string' && item.images.preview) return item.images.preview;
        if (typeof item.image === 'string' && item.image) return item.image;
        if (item.images?.preview && typeof item.images.preview === 'object') {
            const nested = item.images.preview.preview || item.images.preview.original || item.images.preview.url;
            if (nested) return nested;
        }
        if (item.selectedWeight?.images?.length > 0) {
            const sw = item.selectedWeight.images[0];
            if (typeof sw === 'string') return sw;
            if (typeof sw?.preview === 'string') return sw.preview;
        }
        if (item.productOptions?.length > 0) {
            const firstOption = item.productOptions[0];
            if (firstOption.images?.length > 0) {
                const img = firstOption.images[0];
                if (typeof img === 'string') return img;
                if (typeof img?.preview === 'string') return img.preview;
            }
        }
        return require("../../../src/no-image-icon-23494.png");
    };

    // Read activeTab from localStorage on mount
    const [activeTab, setActiveTab] = useState(
        localStorage.getItem("activeTab") || "Received"
    );

    // Initialize filters.filterTerm based on activeTab
    const initialFilterTerm = (() => {
        const tabObj = ORDER_TABS.find(t => t.label === (localStorage.getItem("activeTab") || "Received"));
        return tabObj ? tabObj.apiValue : (localStorage.getItem("activeTab") || "Received");
    })();

    const [searchTerm, setSearchTerm] = useState("");
    const [filters, setFilters] = useState({ filterTerm: initialFilterTerm, isActive: [true, false] });
    const [orders, setOrders] = useState([]);
    const [loadingOrders, setLoadingOrders] = useState(true);

    const [showFilterPopup, setShowFilterPopup] = useState(false);
    const [showSortPopup, setShowSortPopup] = useState(false);

    const [dateRange, setDateRange] = useState([null, null]);
    const [startDate, endDate] = dateRange;

    const [showStatusModal, setShowStatusModal] = useState(false);
    const [selectedOrderId, setSelectedOrderId] = useState(null);
    const [selectedStatus, setSelectedStatus] = useState(null);

    // Online / Offline / Marketplace (same card UI; marketplace uses dedicated API data)
    const [orderType, setOrderType] = useState(() => sessionStorage.getItem("ordersOrderType") || "online");

    // New state for branch filtering
    const [selectedBranch, setSelectedBranch] = useState(null);
    const [branchOptions, setBranchOptions] = useState([]);
    const [loadingBranches, setLoadingBranches] = useState(false);
    const [canSwitchBranch, setCanSwitchBranch] = useState(true); // New state to control branch switching

    // New state for order transfer
    const [showTransferModal, setShowTransferModal] = useState(false);
    const [orderToTransfer, setOrderToTransfer] = useState(null);

    // State for assign driver
    const [showAssignDriverModal, setShowAssignDriverModal] = useState(false);
    const [orderToAssignDriver, setOrderToAssignDriver] = useState(null);
    const [availableDrivers, setAvailableDrivers] = useState([]);
    const [selectedDriverId, setSelectedDriverId] = useState("");
    const [loadingDrivers, setLoadingDrivers] = useState(false);

    // State for auto-confirm online orders toggle
    const [autoConfirmEnabled, setAutoConfirmEnabled] = useState(false);
    const [togglingAutoConfirm, setTogglingAutoConfirm] = useState(false);

    // State for auto-assign (broadcast) orders toggle
    const [autoAssignEnabled, setAutoAssignEnabled] = useState(false);
    const [togglingAutoAssign, setTogglingAutoAssign] = useState(false);

    // State for order counts by status
    const [orderCounts, setOrderCounts] = useState({
        All: 0,
        Received: 0,
        Confirmed: 0,
        Shipped: 0,
        "Out For Delivery": 0,
        Delivered: 0,
        Cancelled: 0
    });

    // Pagination state for virtual scroll
    const [currentPage, setCurrentPage] = useState(0);
    const [hasMoreOrders, setHasMoreOrders] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const PAGE_SIZE = 12;

    const [deliveredTotal, setDeliveredTotal] = useState(null);
    const [deliveredTotalLoading, setDeliveredTotalLoading] = useState(false);

    const fetchOrders = async (params, page = 0, append = false) => {
        if (append) {
            setIsLoadingMore(true);
        } else {
            setLoadingOrders(true);
            setCurrentPage(0);
            setHasMoreOrders(true);
        }
        try {
            // Always send searchTerm in params
            const requestParams = { ...params };

            // Add branch filter if specific branch is selected
            if (selectedBranch && selectedBranch.value !== 'all') {
                requestParams.selectedBranchId = selectedBranch.value;
            }

            // Online / offline filtering (marketplace uses getMarketplaceOrders instead)
            if (orderType === "online") {
                requestParams.isOffline = false;
            } else if (orderType === "offline") {
                requestParams.isOffline = true;
            }

            // If filterTerm is "online" or "offline", override with orderType
            if (requestParams.filterTerm === "online" || requestParams.filterTerm === "offline") {
                requestParams.filterTerm = "All"; // Reset to get all orders of the selected type
            }

            // Add pagination parameters
            requestParams.skip = page * PAGE_SIZE;
            requestParams.limit = PAGE_SIZE;

            // Marketplace (B2B) — same card UI, different collection/API
            if (orderType === "marketplace") {
                const mpRes = await getMarketplaceOrders({
                    filterTerm: requestParams.filterTerm || "All",
                    searchTerm: requestParams.searchTerm || "",
                    from: requestParams.from,
                    to: requestParams.to,
                    skip: requestParams.skip,
                    limit: requestParams.limit,
                });
                if (mpRes?.success) {
                    if (mpRes.counts && !append) {
                        setOrderCounts({
                            All: mpRes.counts.All || 0,
                            Received: mpRes.counts.Received || 0,
                            Confirmed: mpRes.counts.Confirmed || 0,
                            Packed: mpRes.counts.Packed || 0,
                            Shipped: mpRes.counts.Shipped || 0,
                            "Out For Delivery": 0,
                            Delivered: mpRes.counts.Delivered || 0,
                            Cancelled: mpRes.counts.Cancelled || 0,
                        });
                    }
                    const mappedOrders = (mpRes.data || []).map((order) =>
                        adaptMarketplaceOrderForCard(order, formatDateToLong, getOrderItemImage)
                    );
                    setHasMoreOrders((mpRes.data || []).length === PAGE_SIZE);
                    if (append) setOrders((prev) => [...prev, ...mappedOrders]);
                    else setOrders(mappedOrders);
                } else if (!append) {
                    setOrders([]);
                    setHasMoreOrders(false);
                }
                return;
            }

            const response = await getOrders(requestParams);
            console.log("Orders Response:", response);

            if (response.success) {
                // Update order counts from response (only on initial load)
                if (response.counts && !append) {
                    setOrderCounts(response.counts);
                }

                const mappedOrders = response.data.map((order) => {
                    const allItems = order.productsInfo || [];
                    const billableItems = allItems.filter(
                        (item) => !item.isCancelled && !item.isReturned && (item.quantity ?? 0) > 0
                    );
                    const displayItems = billableItems.length > 0 ? billableItems : allItems.filter((item) => !item.isCancelled);
                    const { deliveryCharges, couponDiscount, freeCashDiscount } = getFinancialDiscounts(order.financial);
                    const billSummary = order.financial?.billSummary;

                    const discountedTotal = displayItems.reduce((sum, item) => {
                        const price = item.selectedWeight?.discountPrice || item.pricing?.discountPrice || 0;
                        const quantity = item.quantity ?? 0;
                        return sum + (price * quantity);
                    }, 0);

                    const totalAmount = billSummary?.finalAmount
                        ?? order.financial?.finalAmount
                        ?? calculatePayableFromDiscountedTotal(discountedTotal, order.financial);

                    const activeUnitCount = billableItems.reduce((s, item) => s + (item.quantity ?? 0), 0);
                    const firstBillable = billableItems[0] || displayItems[0] || allItems[0];

                    return {
                        _id: order._id,
                        saleId: order.saleId || order.orderId,
                        name: firstBillable?.productName || order.productsInfo[0]?.productName || "N/A",
                        date: formatDateToLong(order.orderDate || order.orderPlacedOn, true),
                        description: firstBillable?.description || order.productsInfo[0]?.description || "No description available",
                        amount: order.financial?.subtotal || 0,
                        quantity: firstBillable?.quantity ?? order.productsInfo[0]?.quantity ?? 0,
                        itemsCount: activeUnitCount || billableItems.length || order.productsInfo.length,
                        lineItemsCount: billableItems.length,
                        status: order.status || order.orderStatus,
                        firstItemDiscountPrice: firstBillable?.selectedWeight?.discountPrice || firstBillable?.pricing?.discountPrice || 0,
                        image: getOrderItemImage(firstBillable || {}),
                        productsInfo: billableItems.length > 0 ? billableItems : displayItems,
                        totalAmount: totalAmount,
                        deliveryCharges: deliveryCharges,
                        couponDiscount: couponDiscount,
                        freeCashDiscount: freeCashDiscount,
                        hasPartialReturn: order.hasPartialReturn || false,
                        // Add customer info for display
                        customerName: order.customer?.customerName || "N/A",
                        // Add cancellation info
                        cancellationInfo: order.cancellationInfo || null,
                        customerPhone: order.customer?.customerPhone || "N/A",
                        // Add offline flag
                        isOffline: order.isOffline || false,
                        // Add manual delivery flag
                        isManualDelivery: order.isManualDelivery || false,
                        // Add assigned driver info
                        deliveryDriver: order.deliveryDriver || null,
                        // Add branch information for transfer
                        branchId: order.branch?.branchId || null,
                        branchName: order.branch?.branchName || "N/A",
                    };
                });

                // Check if there are more orders to load
                setHasMoreOrders(response.data.length === PAGE_SIZE);

                if (append) {
                    setOrders(prev => [...prev, ...mappedOrders]);
                } else {
                    setOrders(mappedOrders);
                }
            } else {
                if (!append) {
                    setOrders([]);
                    setOrderCounts({
                        All: 0,
                        Received: 0,
                        Confirmed: 0,
                        Shipped: 0,
                        "Out For Delivery": 0,
                        Delivered: 0,
                        Cancelled: 0
                    });
                }
                setHasMoreOrders(false);
            }
        } catch (error) {
            console.error("Error fetching orders:", error);
            if (!append) {
                setOrders([]);
                setOrderCounts({
                    All: 0,
                    Received: 0,
                    Confirmed: 0,
                    Shipped: 0,
                    "Out For Delivery": 0,
                    Delivered: 0,
                    Cancelled: 0
                });
            }
            setHasMoreOrders(false);
        } finally {
            setLoadingOrders(false);
            setIsLoadingMore(false);
        }
    };

    // Load more orders when scrolling
    const loadMoreOrders = () => {
        if (isLoadingMore || !hasMoreOrders || loadingOrders) return;
        const nextPage = currentPage + 1;
        setCurrentPage(nextPage);
        fetchOrders({ searchTerm, ...filters }, nextPage, true);
    };

    // Handle scroll for virtual scrolling
    const handleScroll = (e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        if (scrollHeight - scrollTop - clientHeight < 100 && !isLoadingMore && hasMoreOrders) {
            loadMoreOrders();
        }
    };

    useEffect(() => {
        const debounceTimer = setTimeout(() => {
            // Always send searchTerm in payload
            fetchOrders({ searchTerm, ...filters }, 0, false);
        }, 500);
        return () => clearTimeout(debounceTimer);
        // Debounced refetch on filter/search changes; fetchOrders identity is stable enough via closure.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTerm, filters, orderType, selectedBranch]);

    useEffect(() => {
        let cancelled = false;
        const loadDeliveredTotal = async () => {
            if (filters.filterTerm !== "Delivered" || orderType === "marketplace") {
                setDeliveredTotal(null);
                return;
            }
            setDeliveredTotalLoading(true);
            try {
                const body = {};
                if (orderType === "online") body.isOffline = false;
                else if (orderType === "offline") body.isOffline = true;
                if (filters.from && filters.to) {
                    body.from = filters.from;
                    body.to = filters.to;
                }
                if (selectedBranch && selectedBranch.value !== "all") {
                    body.selectedBranchId = selectedBranch.value;
                }
                const res = await getDeliveredOrdersTotals(body);
                if (!cancelled && res.success && res.data) {
                    setDeliveredTotal(res.data);
                }
            } catch (e) {
                if (!cancelled) setDeliveredTotal(null);
            } finally {
                if (!cancelled) setDeliveredTotalLoading(false);
            }
        };
        loadDeliveredTotal();
        return () => {
            cancelled = true;
        };
    }, [filters.filterTerm, filters.from, filters.to, orderType, selectedBranch]);

    // Fetch branches on mount
    useEffect(() => {
        const fetchBranches = async () => {
            setLoadingBranches(true);
            try {
                const response = await apiRequest(
                    '/clients/getBranchNamesForUser',
                    'POST',
                    {}
                );

                if (response.success && response.data) {
                    const canSwitch = response.canSwitchBranch !== false;
                    const masterBranch = response.data.find((branch) => branch.masterAccount === true);
                    let masterMarketplace = !!masterBranch?.enableMarketplace;
                    if (!masterBranch && response.data[0]?.masterId) {
                        const masterResp = await fetchBranchDataById(response.data[0].masterId);
                        masterMarketplace = !!masterResp?.data?.enableMarketplace;
                    }

                    const branchFlags = (branch) => ({
                        enableOnlineStore: branch.enableOnlineStore || false,
                        enableOfflineStore: branch.enableOfflineStore || false,
                        enableMarketplace: branch.masterAccount
                            ? !!branch.enableMarketplace
                            : !!branch.enableMarketplace && masterMarketplace,
                    });

                    // If user can't switch branches, show only their branch name as text
                    if (!canSwitch && response.data.length === 1) {
                        const branch = response.data[0];
                        const flags = branchFlags(branch);
                        setSelectedBranch({
                            value: branch._id,
                            label: branch.branchName,
                            isMaster: !!branch.masterAccount,
                            ...flags,
                        });
                        setBranchOptions([{
                            value: branch._id,
                            label: branch.branchName,
                            isMaster: !!branch.masterAccount,
                            ...flags,
                        }]);
                        setCanSwitchBranch(false);
                    } else {
                        // User can switch branches - show dropdown
                        setCanSwitchBranch(true);

                        // Create options array with "All Branches (Master View)" first, then other active branches
                        const options = [
                            {
                                value: 'all',
                                label: 'All Branches (Master View)',
                                isMaster: true,
                                enableOnlineStore: masterBranch?.enableOnlineStore || false,
                                enableOfflineStore: masterBranch?.enableOfflineStore || false,
                                enableMarketplace: masterMarketplace,
                                autoConfirmOnlineOrders: masterBranch?.autoConfirmOnlineOrders || false,
                            },
                        ];

                        // Add other branches (excluding master account)
                        const otherBranches = response.data
                            .filter((branch) => !branch.masterAccount)
                            .map((branch) => ({
                                value: branch._id,
                                label: branch.branchName,
                                isMaster: false,
                                ...branchFlags(branch),
                            }));

                        options.push(...otherBranches);
                        setBranchOptions(options);

                        // Set default to "All Branches (Master View)"
                        setSelectedBranch(options[0]);
                        setAutoConfirmEnabled(masterBranch?.autoConfirmOnlineOrders || false);
                        // Load auto-broadcast setting from delivery settings
                        try {
                            const delSettings = await getDeliverySettings();
                            if (delSettings.success && delSettings.data) {
                                setAutoAssignEnabled(delSettings.data.autoBroadcastToDrivers || false);
                            }
                        } catch (e) {
                            console.error('Failed to load delivery settings:', e);
                        }
                        if (options[0].enableOnlineStore && !options[0].enableOfflineStore) setOrderType('online');
                        if (options[0].enableOfflineStore && !options[0].enableOnlineStore) setOrderType('offline');
                    }
                }
            } catch (error) {
                console.error("Error fetching branches:", error);
            } finally {
                setLoadingBranches(false);
            }
        };

        fetchBranches();
    }, []);

    // When selectedBranch changes, update orderType default based on enabled store flags
    useEffect(() => {
        if (!selectedBranch) return;
        const enableOnline = selectedBranch.enableOnlineStore ?? (selectedBranch?.isMaster ? true : false);
        const enableOffline = selectedBranch.enableOfflineStore ?? (selectedBranch?.isMaster ? true : false);
        if (enableOnline && !enableOffline) setOrderType('online');
        if (enableOffline && !enableOnline) setOrderType('offline');
    }, [selectedBranch]);

    useEffect(() => {
        if (startDate && endDate) {
            setFilters(prev => ({ ...prev, from: startDate, to: endDate }));
            setShowFilterPopup(false);
        }
    }, [startDate, endDate]);

    // Update localStorage whenever activeTab changes
    useEffect(() => {
        localStorage.setItem("activeTab", activeTab);
    }, [activeTab]);

    const handleClearDateFilter = () => {
        setDateRange([null, null]);
        setFilters(prev => {
            const newFilters = { ...prev };
            delete newFilters.from;
            delete newFilters.to;
            return newFilters;
        });
    };

    const handleNavigateOrderOverview = (orderId) => {
        if (orderType === "marketplace") {
            navigate(`/orderOverview/${orderId}?source=marketplace`);
            return;
        }
        navigate(`/orderOverview/${orderId}`);
    };

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
    };

    const handleTabChange = (tabLabel) => {
        setActiveTab(tabLabel);
        localStorage.setItem("activeTab", tabLabel); // Persist tab selection
        const tabObj = ORDER_TABS.find(t => t.label === tabLabel);
        const apiTab = tabObj ? tabObj.apiValue : tabLabel;
        const newFilters = { ...filters, filterTerm: apiTab };
        setFilters(newFilters);
    };

    // Sort logic refactored to match Products page
    const handleClearSortSelection = () => {
        const newFilters = { ...filters };
        delete newFilters.sort;
        delete newFilters.sortValue;
        setFilters(newFilters);
        setShowSortPopup(false);
    };

    const handleSortChange = (sortKey, sortValue) => {
        setFilters((prev) => ({ ...prev, sort: sortKey, sortValue }));
        setShowSortPopup(false);
    };

    // Open status modal and bind selected status
    const handleOpenStatusModal = (orderId, status) => {
        // Use the correct status from the order object
        const order = orders.find(o => o._id === orderId);
        setSelectedOrderId(orderId);
        setSelectedStatus(order?.status || order?.orderStatus || "Received");
        setShowStatusModal(true);
    };

    // Handle status change API call
    const handleChangeStatus = async (newStatus) => {
        if (!selectedOrderId) return;
        try {
            const order = orders.find(o => o._id === selectedOrderId);
            if (orderType === "marketplace" || order?.isMarketplaceOrder) {
                const response = await updateMarketplaceOrderStatus({
                    _id: selectedOrderId,
                    status: newStatus,
                });
                if (response?.success) {
                    setOrders(prev =>
                        prev.map(o =>
                            o._id === selectedOrderId ? { ...o, status: newStatus } : o
                        )
                    );
                    setSelectedStatus(newStatus);
                    toast.success(`Order marked ${newStatus}`);
                    fetchOrders({ searchTerm, ...filters }, 0, false);
                } else {
                    toast.error(response?.message || "Failed to update status");
                }
                return;
            }
            const payload = {
                _id: selectedOrderId,
                statusFrom: order.status,
                statusTo: newStatus,
            };
            const response = await changeOrderStatus(payload);
            if (response?.success) {
                setOrders(prev =>
                    prev.map(o =>
                        o._id === selectedOrderId ? { ...o, status: newStatus } : o
                    )
                );
                setSelectedStatus(newStatus);
            }
        } catch (error) {
            console.error("Error updating status:", error);
            toast.error(error?.message || "Failed to update status");
        } finally {
            setShowStatusModal(false);
            setSelectedOrderId(null);
        }
    };

    // Handle opening transfer modal
    const handleOpenTransferModal = (order, e) => {
        e.stopPropagation(); // Prevent navigation to order details
        setOrderToTransfer(order);
        setShowTransferModal(true);
    };

    // Assign Driver handlers
    const handleOpenAssignDriverModal = async (order, e) => {
        e.stopPropagation();
        setOrderToAssignDriver(order);
        setShowAssignDriverModal(true);
        setSelectedDriverId("");
        try {
            setLoadingDrivers(true);
            const res = await getAvailableDrivers();
            if (res.success) {
                setAvailableDrivers(res.data || []);
            }
        } catch (err) {
            console.error("Failed to load drivers:", err);
        } finally {
            setLoadingDrivers(false);
        }
    };

    const handleAssignDriver = async () => {
        if (!selectedDriverId || !orderToAssignDriver) return;
        try {
            const res = await assignDriverToOrder({
                orderId: orderToAssignDriver._id,
                driverId: selectedDriverId,
            });
            if (res.success) {
                const driver = availableDrivers.find(d => d._id === selectedDriverId);
                // Immediately update the order in state so the badge appears without a full re-fetch
                setOrders(prev => prev.map(o =>
                    o._id === orderToAssignDriver._id
                        ? { ...o, deliveryDriver: { driverName: driver?.name || '', driverPhone: driver?.phone || '' } }
                        : o
                ));
                toast.success("Driver assigned successfully!");
                setShowAssignDriverModal(false);
                setOrderToAssignDriver(null);
            } else {
                toast.error(res.message || "Failed to assign driver");
            }
        } catch (err) {
            toast.error("Failed to assign driver");
        }
    };

    // Handle transfer completion
    const handleTransferComplete = (result) => {
        if (result.success) {
            // Refresh orders list (reset to page 0)
            fetchOrders({ searchTerm, ...filters }, 0, false);
            toast.success(result.message || 'Order transferred successfully!');
        }
    };

    // Handle auto-confirm toggle
    const handleToggleAutoConfirm = async () => {
        setTogglingAutoConfirm(true);
        try {
            const newValue = !autoConfirmEnabled;
            const res = await toggleAutoConfirmOnlineOrders({ autoConfirmOnlineOrders: newValue });
            if (res.success) {
                setAutoConfirmEnabled(newValue);
            } else {
                toast.error(res.message || "Failed to update setting");
            }
        } catch (err) {
            toast.error("Failed to update auto-confirm setting");
        } finally {
            setTogglingAutoConfirm(false);
        }
    };

    // Handle auto-assign (broadcast) toggle
    const handleToggleAutoAssign = async () => {
        setTogglingAutoAssign(true);
        try {
            const newValue = !autoAssignEnabled;
            const res = await updateDeliverySettings({ autoBroadcastToDrivers: newValue });
            if (res.success) {
                setAutoAssignEnabled(newValue);
            } else {
                toast.error(res.message || "Failed to update setting");
            }
        } catch (err) {
            toast.error("Failed to update auto-assign setting");
        } finally {
            setTogglingAutoAssign(false);
        }
    };

    return (
        <div className="h-screen overflow-hidden max-h-fit bg-white">
            <div className={`bg-white px-6 py-6 sticky z-10 space-y-4 ${classOuterDiv}`}>
                <div className="flex justify-between items-center">
                    <h1 className="text-3xl font-bold">Orders</h1>

                    {/* Branch Selector or Branch Name Display */}
                    <div className="flex items-center gap-3">
                        <div className="w-72">
                            {canSwitchBranch ? (
                                <Select
                                    options={branchOptions}
                                    value={selectedBranch}
                                    placeholder="Select Branch"
                                    onChange={(option) => setSelectedBranch(option)}
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    isLoading={loadingBranches}
                                    menuPortalTarget={document.body}
                                    styles={{
                                        control: (base) => ({
                                            ...base,
                                            borderRadius: '0.5rem',
                                            borderColor: '#d1d5db',
                                            '&:hover': {
                                                borderColor: '#3b82f6'
                                            }
                                        }),
                                        menuPortal: (base) => ({ ...base, zIndex: 9999 })
                                    }}
                                />
                            ) : (
                                <div className="px-4 py-2 bg-gray-100 rounded-lg border border-gray-300">
                                    <span className="text-sm text-gray-500">Branch: </span>
                                    <span className="text-base font-semibold text-gray-700">
                                        {selectedBranch?.label || 'Loading...'}
                                    </span>
                                </div>
                            )}
                        </div>

                        <button
                            onClick={() => fetchOrders({ searchTerm, ...filters }, 0, false)}
                            disabled={loadingOrders}
                            className="flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors disabled:opacity-50"
                        >
                            <FaSync className={loadingOrders ? "animate-spin" : ""} />
                            Refresh
                        </button>
                    </div>
                </div>

                <div className="sticky top-[4.5rem] z-10 bg-white/80 backdrop-blur-md space-y-5">
                    {/* Online/Offline Order Type Tabs */}
                    <div className="mb-6 border-b border-gray-200">
                        <div className="flex space-x-8 items-center justify-between">
                            <div className="flex space-x-8">
                                {(() => {
                                    const enableOnline = selectedBranch?.enableOnlineStore ?? (selectedBranch?.isMaster ? true : false);
                                    const enableOffline = selectedBranch?.enableOfflineStore ?? (selectedBranch?.isMaster ? true : false);
                                    const enableMarketplace = selectedBranch?.enableMarketplace ?? false;

                                    const switchType = (type) => {
                                        setOrderType(type);
                                        sessionStorage.setItem("ordersOrderType", type);
                                        setActiveTab('Received');
                                        localStorage.setItem("activeTab", 'Received');
                                        setFilters(prev => ({ ...prev, filterTerm: 'Received' }));
                                    };
                                    const tabBtn = (type, label) => (
                                        <button
                                            key={type}
                                            type="button"
                                            onClick={() => switchType(type)}
                                            className={`pb-2 text-base font-medium transition-colors ${orderType === type
                                                ? (type === 'marketplace' ? 'text-violet-600 border-b-2 border-violet-600' : 'text-orange-500 border-b-2 border-orange-500')
                                                : 'text-gray-500 hover:text-gray-700'
                                                }`}
                                        >
                                            {label}
                                        </button>
                                    );

                                    const available = [];
                                    if (enableOnline) available.push(['online', 'Online Orders']);
                                    if (enableOffline) available.push(['offline', 'Offline Orders']);
                                    if (enableMarketplace) available.push(['marketplace', 'Marketplace Orders']);

                                    // A single sales tab with no marketplace stays as a static label (unchanged UX)
                                    if (available.length === 1 && available[0][0] !== 'marketplace') {
                                        return (<div className="pb-2 text-base font-medium text-orange-600">{available[0][1]}</div>);
                                    }
                                    return <>{available.map(([type, label]) => tabBtn(type, label))}</>;
                                })()}
                            </div>
                            {/* Auto-confirm toggle - visible for master accounts when online store is enabled */}
                            {canSwitchBranch && (selectedBranch?.enableOnlineStore || selectedBranch?.isMaster) && (
                                <div className="flex items-center gap-4 pb-2 flex-wrap">
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm text-gray-600">Auto-Confirm Online Orders</span>
                                        <button
                                            type="button"
                                            onClick={handleToggleAutoConfirm}
                                            disabled={togglingAutoConfirm}
                                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${autoConfirmEnabled ? 'bg-green-500' : 'bg-gray-300'
                                                } ${togglingAutoConfirm ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                        >
                                            <span
                                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${autoConfirmEnabled ? 'translate-x-6' : 'translate-x-1'
                                                    }`}
                                            />
                                        </button>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm text-gray-600">Auto-Assign Orders</span>
                                        <button
                                            type="button"
                                            onClick={handleToggleAutoAssign}
                                            disabled={togglingAutoAssign}
                                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${autoAssignEnabled ? 'bg-green-500' : 'bg-gray-300'
                                                } ${togglingAutoAssign ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                        >
                                            <span
                                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${autoAssignEnabled ? 'translate-x-6' : 'translate-x-1'
                                                    }`}
                                            />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="flex items-center overflow-x-auto space-x-3 scrollbar-hide p-1">
                        <div className="flex space-x-3">
                            {(orderType === 'marketplace' ? MARKETPLACE_ORDER_TABS : ORDER_TABS.slice(2)).map((tab) => {
                                // Get count for this tab
                                const tabCount = orderCounts[tab.apiValue] || 0;
                                return (
                                    <motion.div
                                        key={tab.label}
                                        className={`px-6 py-2 rounded-full text-sm font-semibold cursor-pointer transition min flex items-center gap-2 ${activeTab === tab.label
                                            ? "bg-blue-600 text-white shadow"
                                            : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                                            }`}
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                        onClick={() => handleTabChange(tab.label)}
                                    >
                                        {tab.label}
                                        {tabCount > 0 && (
                                            <span className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-bold rounded-full ${activeTab === tab.label
                                                    ? "bg-white text-blue-600"
                                                    : "bg-blue-100 text-blue-600"
                                                }`}>
                                                {tabCount > 99 ? '99+' : tabCount}
                                            </span>
                                        )}
                                    </motion.div>
                                );
                            })}
                        </div>
                    </div>

                    {filters.filterTerm === "Delivered" && (
                        <div className="mt-2 px-1">
                            {deliveredTotalLoading ? (
                                <span className="text-xs text-gray-500">Loading delivered totals…</span>
                            ) : deliveredTotal ? (
                                <div className="inline-flex flex-wrap items-center gap-2 rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-900">
                                    <span className="font-semibold">Delivered orders total</span>
                                    <span className="font-mono font-bold">
                                        ₹{Number(deliveredTotal.totalFinalAmount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </span>
                                    <span className="text-emerald-700">
                                        ({deliveredTotal.orderCount || 0} order{(deliveredTotal.orderCount || 0) === 1 ? "" : "s"}
                                        {filters.from && filters.to ? " in selected date range" : " — all time"})
                                    </span>
                                </div>
                            ) : null}
                        </div>
                    )}

                    <div className="flex flex-col md:flex-row md:items-center md:space-x-4 relative">
                        <div className="w-full relative">
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={handleSearchChange}
                                placeholder="Search Orders"
                                className="border border-gray-300 rounded-full py-2 pl-11 pr-4 w-full text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm hover:shadow-md transition-shadow duration-300"
                            />
                            <div className="absolute top-1/2 left-4 transform -translate-y-1/2 text-gray-400">
                                <FaSearch className="text-lg" />
                            </div>
                        </div>

                        <div className="flex flex-row w-full md:w-auto max-md:mt-5">
                            <motion.div
                                key="filters"
                                data-filter-button
                                onClick={(e) => {
                                    if (!showFilterPopup) {
                                        setShowFilterPopup(true);
                                    } else {
                                        setShowFilterPopup(false);
                                    }
                                    if (showSortPopup) setShowSortPopup(false);
                                }}
                                className="flex items-center px-4 py-2 rounded-full hover:shadow-md text-sm font-semibold transition cursor-pointer bg-white text-gray-700 border border-gray-300 shadow hover:bg-gray-100 hover:text-gray-900 focus:ring-2 focus:ring-blue-500 relative"
                                whileHover={{ scale: 1.05 }}
                            >
                                <IoFilter className="mr-2 text-lg" />
                                Filters
                                {showFilterPopup && (
                                    <motion.div
                                        initial={{ opacity: 0, y: -10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -10 }}
                                        onClick={(e) => e.stopPropagation()}
                                        className="absolute top-full -left-20 max-md:left-0 mt-2 w-fit bg-white border border-gray-200 shadow-lg p-4"
                                    >
                                        <DatePicker
                                            selectsRange
                                            startDate={startDate}
                                            endDate={endDate}
                                            onChange={(update) => {
                                                setDateRange(update);
                                            }}
                                            isClearable={true}
                                            inline
                                            shouldCloseOnSelect={false}
                                        />
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleClearDateFilter();
                                                setShowFilterPopup(false);
                                            }}
                                            className="mt-2 w-full px-4 py-2 bg-red-400 text-white rounded-md transition-colors duration-300 hover:bg-red-600 text-sm"
                                        >
                                            Clear Date Filter
                                        </button>
                                    </motion.div>
                                )}
                            </motion.div>

                            {/* Sort Button (refactored to match Products page) */}
                            <motion.div
                                key="sort"
                                onClick={() => {
                                    setShowSortPopup(!showSortPopup);
                                    if (showFilterPopup) setShowFilterPopup(false);
                                }}
                                className="flex items-center px-4 py-2 ml-4 rounded-full cursor-pointer hover:shadow-md text-sm font-semibold transition bg-white text-gray-700 border border-gray-300 shadow hover:bg-gray-100 hover:text-gray-900 focus:ring-2 focus:ring-blue-500 relative"
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                            >
                                <FaSort className="mr-2 text-lg" />
                                Sort
                                {showSortPopup && (
                                    <motion.div
                                        initial={{ opacity: 0, y: -10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -10 }}
                                        className="absolute top-full right-0 mt-2 w-52 bg-white border border-gray-200 rounded-md shadow-lg"
                                    >
                                        {filters.sort && (
                                            <button
                                                onClick={handleClearSortSelection}
                                                className="w-full text-left px-4 py-2 bg-red-400 text-white transition-colors duration-300 hover:bg-red-600 text-sm"
                                            >
                                                Clear Sort
                                            </button>
                                        )}
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleSortChange("id", 1);
                                            }}
                                            className={`w-full flex justify-between text-left px-4 py-2 transition-colors duration-300 ${filters.sort === "id" && filters.sortValue === 1
                                                ? "bg-blue-500 text-white"
                                                : "hover:bg-gray-100 text-gray-700"
                                                }`}
                                        >
                                            <span>Sort by ID</span>
                                            <div className="flex items-center gap-1.5">
                                                <span className="text-xs opacity-80">(A to Z)</span>
                                                <span className={`${filters.sort === "id" && filters.sortValue === 1 ? 'text-white' : 'text-gray-600'}`}>
                                                    ↓
                                                </span>
                                            </div>
                                        </button>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleSortChange("id", -1);
                                            }}
                                            className={`w-full flex justify-between text-left px-4 py-2 transition-colors duration-300 ${filters.sort === "id" && filters.sortValue === -1
                                                ? "bg-blue-500 text-white"
                                                : "hover:bg-gray-100 text-gray-700"
                                                }`}
                                        >
                                            <span>Sort by ID</span>
                                            <div className="flex items-center gap-1.5">
                                                <span className="text-xs opacity-80">(Z to A)</span>
                                                <span className={`${filters.sort === "id" && filters.sortValue === -1 ? 'text-white' : 'text-gray-600'}`}>
                                                    ↑
                                                </span>
                                            </div>
                                        </button>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleSortChange("name", 1);
                                            }}
                                            className={`w-full flex justify-between text-left px-4 py-2 transition-colors duration-300 ${filters.sort === "name" && filters.sortValue === 1
                                                ? "bg-blue-500 text-white"
                                                : "hover:bg-gray-100 text-gray-700"
                                                }`}
                                        >
                                            <span>Sort by Name</span>
                                            <div className="flex items-center gap-1.5">
                                                <span className="text-xs opacity-80">(A to Z)</span>
                                                <span className={`${filters.sort === "name" && filters.sortValue === 1 ? 'text-white' : 'text-gray-600'}`}>
                                                    ↓
                                                </span>
                                            </div>
                                        </button>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleSortChange("date", -1);
                                            }}
                                            className={`w-full flex justify-between text-left px-4 py-2 transition-colors duration-300 ${filters.sort === "date" && filters.sortValue === -1
                                                ? "bg-blue-500 text-white"
                                                : "hover:bg-gray-100 text-gray-700"
                                                }`}
                                        >
                                            <span>Sort by Date</span>
                                            <div className="flex items-center gap-1.5">
                                                <span className="text-xs opacity-80">(Newest First)</span>
                                                <span className={`${filters.sort === "date" && filters.sortValue === -1 ? 'text-white' : 'text-gray-600'}`}>
                                                    ↑
                                                </span>
                                            </div>
                                        </button>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleSortChange("date", 1);
                                            }}
                                            className={`w-full flex justify-between text-left px-4 py-2 transition-colors duration-300 ${filters.sort === "date" && filters.sortValue === 1
                                                ? "bg-blue-500 text-white"
                                                : "hover:bg-gray-100 text-gray-700"
                                                }`}
                                        >
                                            <span>Sort by Date</span>
                                            <div className="flex items-center gap-1.5">
                                                <span className="text-xs opacity-80">(Oldest First)</span>
                                                <span className={`${filters.sort === "date" && filters.sortValue === 1 ? 'text-white' : 'text-gray-600'}`}>
                                                    ↓
                                                </span>
                                            </div>
                                        </button>
                                    </motion.div>
                                )}
                            </motion.div>
                        </div>
                    </div>
                </div>
            </div>

            {loadingOrders ? (
                <div className="fixed inset-0 flex items-center justify-center bg-white/80 z-50">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                </div>
            ) : (
                <div className="overflow-y-auto h-full max-h-[calc(100vh-330px)] md:max-h-[calc(100vh-258px)] px-6 bg-white scrollbar-thin" onScroll={handleScroll}>
                    <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-3 mb-6 ${classInnerDiv}`}>
                        {orders.length > 0 ? (
                            orders.map((order) => (
                                <motion.div
                                    className="bg-white border shadow-md p-4 rounded-xl hover:shadow-xl"
                                    key={order._id}
                                    onClick={() => handleNavigateOrderOverview(order._id)}
                                >
                                    <motion.div
                                        key={order._id}
                                        className="flex relative cursor-pointer"
                                    >
                                        <div className="flex flex-col gap-1">
                                            <div
                                                className={`absolute top-3 -left-4 shadow-[4px_0px_6px_rgba(0,0,0,0.3)] rounded-full rounded-l-sm px-3 py-1 text-sm font-semibold text-white ${order.status === "Delivered"
                                                    ? "bg-green-500"
                                                    : order.status === "Confirmed"
                                                        ? "bg-blue-500"
                                                        : order.status === "Shipped"
                                                            ? "bg-purple-500"
                                                            : order.status === "Packed"
                                                                ? "bg-amber-500"
                                                            : order.status === "Out For Delivery"
                                                                ? "bg-orange-500"
                                                                : order.status === "Cancelled"
                                                                    ? "bg-red-500"
                                                                    : "bg-yellow-500"
                                                    }`}
                                            >
                                                {order.status === "Out For Delivery"
                                                    ? "OutForDelivery"
                                                    : order.status === "Packed"
                                                        ? "Packed"
                                                        : order.status}
                                            </div>
                                            {order.status === "Cancelled" && order.cancellationInfo?.cancelledBy === "customer" && (
                                                <div className="absolute top-12 -left-4 shadow-md rounded-r-md px-2 py-1 text-xs font-bold text-white bg-gradient-to-r from-red-600 to-red-500 border-l-4 border-red-700">
                                                    ⚠️ By Customer
                                                </div>
                                            )}
                                            {order.isManualDelivery && (
                                                <div className="absolute top-12 -left-4 shadow-md rounded-r-md px-2 py-1 text-xs font-bold text-white bg-gradient-to-r from-orange-500 to-orange-400 border-l-4 border-orange-600"
                                                    style={{ top: order.status === "Cancelled" && order.cancellationInfo?.cancelledBy === "customer" ? '5rem' : '3rem' }}
                                                >
                                                    🚚 Manual Delivery
                                                </div>
                                            )}
                                        </div>
                                        <div className="w-40 h-40 flex-shrink-0">
                                            <img
                                                src={order.image || require("../../../src/no-image-icon-23494.png")}
                                                alt={`Order ${order.name}`}
                                                className="w-full h-full object-cover rounded-lg border"
                                                onError={(e) => {
                                                    e.currentTarget.onerror = null;
                                                    e.currentTarget.src = require("../../../src/no-image-icon-23494.png");
                                                }}
                                            />
                                        </div>

                                        <div className="flex-grow ml-3">
                                            <p className="text-sm text-gray-500 font-semibold">
                                                {order.saleId || "N/A"}
                                            </p>
                                            <h2 className="text-lg font-semibold text-gray-900 line-clamp-1">{order.name}</h2>
                                            <p className="text-sm text-gray-700 mt-1 line-clamp-1">{order.date}</p>
                                            <p className="text-sm text-gray-700 mt-1 line-clamp-1">{order.description}</p>

                                            {/* Action buttons row */}
                                            <div className="flex justify-between items-center mt-2 gap-2">
                                                <p className="text-sm text-gray-700 font-semibold">₹{order.firstItemDiscountPrice}</p>
                                                <div className="flex items-center gap-3">
                                                    {/* Transfer — store orders only */}
                                                    {!order.isMarketplaceOrder && orderType !== 'marketplace' && (
                                                    <button
                                                        onClick={(e) => handleOpenTransferModal(order, e)}
                                                        className="flex items-center gap-1 text-blue-600 hover:text-blue-700 font-semibold text-xs transition-colors"
                                                        title="Transfer to another branch"
                                                    >
                                                        <FaExchangeAlt className="text-sm" />
                                                        <span>Transfer</span>
                                                    </button>
                                                    )}

                                                    {/* Change Status Link */}
                                                    <div className="relative">
                                                        <span
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleOpenStatusModal(order._id, order.status);
                                                            }}
                                                            className="relative text-yellow-600 font-semibold text-xs underline inline-block overflow-hidden cursor-pointer"
                                                        >
                                                            Change Status
                                                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-60 w-full h-full animate-light-slide pointer-events-none"></div>
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="flex justify-between items-center relative overflow-hidden mt-1">
                                                <p className="text-sm text-gray-700 font-semibold">Qty: {order.quantity}</p>
                                                <div
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setExpandedCard((prev) => (prev === order._id ? null : order._id));
                                                    }}
                                                    className="relative"
                                                >
                                                    {/* Show "+N items" only if more than 1 item */}
                                                    {order.lineItemsCount > 1 && (
                                                        <span className="text-green-600 font-semibold text-sm relative">
                                                            +{order.lineItemsCount - 1} items
                                                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-60 w-full h-full animate-light-slide pointer-events-none"></div>
                                                        </span>
                                                    )}
                                                    {order.lineItemsCount <= 1 && (
                                                        <p className="text-md text-gray-700 font-bold">Total: ₹{(order.totalAmount || 0).toFixed(2)}</p>
                                                    )}
                                                </div>
                                            </div>
                                            {/* Delivery & Coupon charges */}
                                            {(order.deliveryCharges > 0 || order.couponDiscount > 0 || order.freeCashDiscount > 0) && (
                                                <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                                                    {order.deliveryCharges > 0 && (
                                                        <span className="text-xs text-blue-600 font-medium">🚚 ₹{order.deliveryCharges.toFixed(2)}</span>
                                                    )}
                                                    {order.couponDiscount > 0 && (
                                                        <span className="text-xs text-green-600 font-medium">🎟️ -₹{order.couponDiscount.toFixed(2)}</span>
                                                    )}
                                                    {order.freeCashDiscount > 0 && (
                                                        <span className="text-xs text-purple-600 font-medium">💸 -₹{order.freeCashDiscount.toFixed(2)}</span>
                                                    )}
                                                </div>
                                            )}
                                            {/* Driver Assigned Badge */}
                                            {order.deliveryDriver?.driverName && (
                                                <div className="flex items-center gap-1 mt-1">
                                                    <FaTruck className="text-xs text-green-600" />
                                                    <span className="text-xs text-green-700 font-semibold">Driver: {order.deliveryDriver.driverName}</span>
                                                </div>
                                            )}

                                            {/* Assign Driver Button - store online/offline only */}
                                            {!order.isMarketplaceOrder && orderType !== 'marketplace' && (order.status === "Confirmed" || order.status === "Shipped") && !order.isManualDelivery && !order.deliveryDriver && (
                                                <button
                                                    onClick={(e) => handleOpenAssignDriverModal(order, e)}
                                                    className="flex items-center gap-1 text-green-600 hover:text-green-700 font-semibold text-xs transition-colors mt-2"
                                                    title="Assign delivery driver"
                                                >
                                                    <FaTruck className="text-sm" />
                                                    <span>Assign Driver</span>
                                                </button>
                                            )}
                                        </div>
                                    </motion.div>

                                    {/* Expanded card: show remaining items with data binding */}
                                    {expandedCard === order._id && (
                                        <div className="mt-4 space-y-3">
                                            {order.productsInfo.slice(1).map((item, idx) => (
                                                <div className="flex items-center space-x-3" key={item._id || idx}>
                                                    <div className="w-16 h-16 flex-shrink-0">
                                                        <img
                                                            src={getOrderItemImage(item)}
                                                            alt={item.productName}
                                                            className="w-16 h-16 object-cover rounded-lg border"
                                                        />
                                                    </div>
                                                    <div>
                                                        <p className="text-sm font-semibold text-gray-800 line-clamp-1">{item.productName}</p>
                                                        <p className="text-sm text-gray-600 line-clamp-1">{item.description}</p>
                                                        <div className="flex space-x-3 font-semibold">
                                                            <p className="text-sm text-gray-700">
                                                                ₹{item.selectedWeight?.discountPrice || item.pricing?.discountPrice || "-"}
                                                            </p>
                                                            <p className="text-sm text-gray-700">Qty: {item.quantity}</p>
                                                            {item.selectedWeight?.weight && (
                                                                <p className="text-sm text-gray-700">
                                                                    ({typeof item.selectedWeight.weight === 'object' ? item.selectedWeight.weight.weightLabel : item.selectedWeight.weight})
                                                                </p>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                            <div className="flex justify-between items-center space-x-3 relative">
                                                <div>
                                                    <p className="text-md text-gray-700 font-bold">Total: ₹{(order.totalAmount || 0).toFixed(2)}</p>
                                                    {(order.deliveryCharges > 0 || order.couponDiscount > 0 || order.freeCashDiscount > 0) && (
                                                        <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                                                            {order.deliveryCharges > 0 && (
                                                                <span className="text-xs text-blue-600 font-medium">🚚 ₹{order.deliveryCharges.toFixed(2)}</span>
                                                            )}
                                                            {order.couponDiscount > 0 && (
                                                                <span className="text-xs text-green-600 font-medium">🎟️ -₹{order.couponDiscount.toFixed(2)}</span>
                                                            )}
                                                            {order.freeCashDiscount > 0 && (
                                                                <span className="text-xs text-purple-600 font-medium">💸 -₹{order.freeCashDiscount.toFixed(2)}</span>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="relative cursor-pointer">
                                                    <span
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            // You can navigate to full details or handle as needed
                                                            handleNavigateOrderOverview(order._id);
                                                        }}
                                                        className="relative text-green-600 font-semibold text-sm underline inline-block overflow-hidden"
                                                    >
                                                        Full Details
                                                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-60 w-full h-full animate-light-slide pointer-events-none"></div>
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </motion.div>
                            ))
                        ) : (
                            <p>No Orders found</p>
                        )}
                    </div>
                    {isLoadingMore && (
                        <div className="flex justify-center py-4">
                            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                        </div>
                    )}
                </div>
            )}

            {/* Status Change Modal */}
            {showStatusModal && selectedOrderId && (
                <StatusChangeModal
                    showStatusModal={showStatusModal}
                    setShowStatusModal={(val) => {
                        setShowStatusModal(val);
                        if (!val) setSelectedOrderId(null);
                    }}
                    selectedStatus={selectedStatus}
                    setSelectedStatus={setSelectedStatus}
                    handleChangeStatus={handleChangeStatus}
                    statusFlow={orderType === "marketplace" ? MARKETPLACE_STATUS_FLOW : STATUS_FLOW}
                />
            )}

            {/* Transfer Order Modal */}
            {showTransferModal && orderToTransfer && (
                <TransferOrderModal
                    isOpen={showTransferModal}
                    onClose={() => {
                        setShowTransferModal(false);
                        setOrderToTransfer(null);
                    }}
                    order={orderToTransfer}
                    branches={branchOptions}
                    currentBranchId={orderToTransfer.branchId || selectedBranch?.value}
                    onTransfer={handleTransferComplete}
                />
            )}

            {/* Assign Driver Modal */}
            {showAssignDriverModal && orderToAssignDriver && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowAssignDriverModal(false)}>
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 m-4" onClick={(e) => e.stopPropagation()}>
                        <h2 className="text-lg font-semibold text-gray-900 mb-1">Assign Delivery Driver</h2>
                        <p className="text-sm text-gray-500 mb-4">Order: {orderToAssignDriver.saleId || orderToAssignDriver._id}</p>

                        {loadingDrivers ? (
                            <p className="text-center py-4 text-gray-500">Loading available drivers...</p>
                        ) : availableDrivers.length === 0 ? (
                            <p className="text-center py-4 text-gray-500">No available drivers found. Add drivers from Delivery Controls.</p>
                        ) : (
                            <div className="space-y-3">
                                <label className="block text-sm font-medium text-gray-700">Select Driver</label>
                                <select
                                    value={selectedDriverId}
                                    onChange={(e) => setSelectedDriverId(e.target.value)}
                                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                >
                                    <option value="">-- Choose a driver --</option>
                                    {availableDrivers.map((d) => (
                                        <option key={d._id} value={d._id}>
                                            {d.name} — {d.vehicleType} ({d.activeOrderCount || 0} active)
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}

                        <div className="flex justify-end gap-3 mt-6">
                            <button
                                onClick={() => { setShowAssignDriverModal(false); setOrderToAssignDriver(null); }}
                                className="px-4 py-2 border rounded-lg text-gray-700 hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleAssignDriver}
                                disabled={!selectedDriverId}
                                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium disabled:opacity-50"
                            >
                                Assign Driver
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* <HelpChatbotWidget /> */}
        </div>
    );
};

export default Orders;